import { ChangeDetectionStrategy, Component, computed, inject } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { Router } from "@angular/router";
import { AccountMarketplaceService, BillingPlanService, BillingService } from "@front/m19-services";
import { ToasterService } from "@front/m19-ui";
import { AjaxError } from "rxjs/ajax";
import { AccountMarketplace, Plan, Response } from "@front/m19-api-client";
import { TranslocoDirective } from "@jsverse/transloco";
import { BillingAccountSelectionComponent } from "@m19-board/settings/billing-settings/billing-account-selection/billing-account-selection.component";
import { map, switchMap, tap } from "rxjs/operators";

@Component({
  selector: "app-selfservice-freemium",
  templateUrl: "./selfservice-freemium.component.html",
  styleUrls: ["selfservice-freemium.component.scss"],
  host: {
    class: "block h-full w-full",
  },
  imports: [TranslocoDirective, BillingAccountSelectionComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SelfServiceFreemiumComponent {
  private router = inject(Router);
  private billingService = inject(BillingService);
  private billingPlanService = inject(BillingPlanService);
  private accountMarketplaceService = inject(AccountMarketplaceService);
  private toasterService = inject(ToasterService);

  private organizationOwner = toSignal(this.billingService.organizationOwner$);
  organization = computed(() => this.organizationOwner()?.organization);
  currentBillingPlan = computed(() => this.organizationOwner()?.getBillingPlan());
  private freemiumBp = toSignal(
    this.billingPlanService.listBillingPlans(Plan.STARTER).pipe(map((billingPlans) => billingPlans[0])),
  );

  activate(organizationResources: AccountMarketplace[]): void {
    const organization = this.organization()!;
    organization.billingPlan = this.freemiumBp();

    this.billingService
      .createSubscription(organization, undefined)
      .pipe(
        tap({
          next: (success: Response) => {
            this.toasterService.success("Subscription successfully created");
          },
          error: (error: AjaxError) => {
            this.toasterService.error(
              error.response ? error.response.message : "Unknown error",
              "Subscription failure",
            );
          },
        }),
        switchMap(() => this.accountMarketplaceService.activateBidder(organizationResources)),
      )
      .subscribe({
        next: () => {
          this.backToBilling();
        },
        error: () => {
          this.backToBilling();
        },
      });
  }

  backToBilling(): void {
    this.router.navigate(["billing"]);
  }

  backToHome(): void {
    this.router.navigate(["dashboard"]);
  }
}
